import { For, Show } from "solid-js";
import { msg } from "@lingui/core/macro";
import { UserPlus, Users } from "lucide-solid";

import { Link } from "@/lib/u-router";
import { useSession } from "@/lib/auth";
import { useDrizzle } from "@/hooks/use-drizzle";
import { usePowerSyncContext } from "@/providers/power-sync-context";
import { useI18n } from "@/providers/i18n-provider";
import { createPowerSyncQuery } from "@famidex/shared/solid";
import { getFollowersQuery, getFollowingQuery, queryKeys } from "@famidex/shared";

type Mode = "followers" | "following";

/**
 * 👥 Seguidores / Seguidos — el grafo social del usuario, local-first [L] y
 * aislado por cuenta (streams `social_follows`). Un único componente responsive
 * cubre ambas pantallas Stitch (`seguidores` / `seguidos`) con un segmentado.
 */
export function FollowListPage(props: { mode: Mode }) {
  const { db } = usePowerSyncContext();
  const drizzle = useDrizzle();
  const session = useSession();
  const { t } = useI18n();
  const { userId } = session;

  const enabled = () => Boolean(drizzle() && userId());

  const rows = createPowerSyncQuery({
    db,
    enabled,
    queryKey: () =>
      props.mode === "followers"
        ? queryKeys.social.followers(userId())
        : queryKeys.social.following(userId()),
    queryFn: () => {
      const d = drizzle();
      const uid = userId();
      if (!d || !uid) {
        return null;
      }
      return props.mode === "followers" ? getFollowersQuery(d, uid) : getFollowingQuery(d, uid);
    },
  });

  const personOf = (row: {
    follower_id: string | null;
    followee_id: string | null;
    follower_name: string | null;
    follower_image: string | null;
    followee_name: string | null;
    followee_image: string | null;
  }) =>
    props.mode === "followers"
      ? { id: row.follower_id, name: row.follower_name, image: row.follower_image }
      : { id: row.followee_id, name: row.followee_name, image: row.followee_image };

  return (
    <div class="flex flex-col gap-6" data-testid="follow-list-page">
      <header>
        <h1 class="text-2xl font-semibold text-foreground">{t(msg`Tu círculo`)}</h1>
        <p class="mt-1 text-sm text-muted-foreground">
          {t(msg`Coleccionistas conectados a tu aldea.`)}
        </p>
      </header>

      {/* Segmentado seguidores / seguidos */}
      <div class="flex gap-1 rounded-full border border-border/60 bg-card/60 p-1 text-sm font-semibold">
        <Link
          to="/social/followers"
          data-testid="tab-followers"
          aria-current={props.mode === "followers" ? "page" : undefined}
          class="flex-1 rounded-full px-4 py-2 text-center transition-colors aria-[current]:bg-primary aria-[current]:text-primary-foreground"
        >
          {t(msg`Seguidores`)}
        </Link>
        <Link
          to="/social/following"
          data-testid="tab-following"
          aria-current={props.mode === "following" ? "page" : undefined}
          class="flex-1 rounded-full px-4 py-2 text-center transition-colors aria-[current]:bg-primary aria-[current]:text-primary-foreground"
        >
          {t(msg`Seguidos`)}
        </Link>
      </div>

      <Show
        when={(rows.data ?? []).length > 0}
        fallback={
          <div
            data-testid="follow-empty"
            class="grid place-items-center gap-2 rounded-xl border border-dashed border-border/70 bg-card/40 py-14 text-center text-muted-foreground"
          >
            <Users class="h-8 w-8" />
            <p class="max-w-xs text-sm">
              {props.mode === "followers"
                ? t(msg`Aún no te sigue nadie. Comparte tu colección para crecer tu aldea.`)
                : t(
                    msg`Todavía no sigues a nadie. Explora la comunidad para descubrir coleccionistas.`,
                  )}
            </p>
            <Link
              to="/community"
              class="mt-1 rounded-full bg-primary px-4 py-2 text-xs font-semibold text-primary-foreground"
            >
              {t(msg`Explorar comunidad`)}
            </Link>
          </div>
        }
      >
        <ul class="flex flex-col gap-2">
          <For each={rows.data}>
            {(row) => {
              const p = personOf(row);
              return (
                <li
                  data-testid="follow-row"
                  class="flex items-center gap-3 rounded-xl border border-border/60 bg-card p-3"
                >
                  <span class="grid h-11 w-11 shrink-0 place-items-center overflow-hidden rounded-full bg-accent text-accent-foreground">
                    <Show when={p.image} fallback={<UserPlus class="h-5 w-5" />}>
                      <img
                        src={p.image ?? ""}
                        alt={p.name ?? ""}
                        loading="lazy"
                        class="h-full w-full object-cover"
                      />
                    </Show>
                  </span>
                  <div class="min-w-0 flex-1">
                    <p class="truncate text-sm font-semibold text-foreground">
                      {p.name ?? t(msg`Coleccionista`)}
                    </p>
                    <p class="truncate text-xs text-muted-foreground">
                      {t(msg`Coleccionista Sylvanian`)}
                    </p>
                  </div>
                </li>
              );
            }}
          </For>
        </ul>
      </Show>
    </div>
  );
}
